import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, Switch, Space, Card, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';

const { TextArea } = Input;
const { Text } = Typography;

interface TriggerConditionSectionProps {
  form: FormInstance;
  triggerType?: string;
  triggerSubtype?: string;
}

// 트리거 조건의 상세 타입 정의
interface TriggerConditionDetail {
  // TIME 트리거
  time_seconds?: number;
  force_skill?: string;
  
  // HP 트리거
  hp_threshold?: number;
  interrupt_current?: boolean;
  
  // SKILL_ACTION 트리거
  skill?: string;
  count?: number;
  replace_with?: string;
  reset_after?: boolean;
  
  // ENVIRONMENT/DISTANCE 트리거
  distance_type?: 'first_aggro' | 'nearest' | 'farthest';
  threshold?: number;
  skill_if_close?: string;
  skill_if_far?: string;
  condition?: string;
  
  // COUNTER 트리거
  counter_name?: string;
  target_count?: number;
  reset_condition?: 'none' | 'on_trigger' | 'phase_change';
}

const TriggerConditionSection: React.FC<TriggerConditionSectionProps> = ({ 
  form, 
  triggerType, 
  triggerSubtype 
}) => {
  const [conditionDetail, setConditionDetail] = useState<TriggerConditionDetail>({});

  // 트리거 타입에 따른 조건 UI 렌더링
  const renderConditionFields = () => {
    if (!triggerType) {
      return <Text type="secondary">트리거 타입을 먼저 선택하세요</Text>;
    }

    switch (triggerType) {
      case 'TIME':
        return (
          <>
            <Form.Item label="시간 (초)" required>
              <InputNumber
                min={0}
                value={conditionDetail.time_seconds}
                onChange={(value) => updateConditionDetail({ time_seconds: value ?? undefined })}
                style={{ width: '100%' }}
              />
            </Form.Item>
            {triggerSubtype === 'BATTLE_TIME' && (
              <Form.Item label="강제 스킬">
                <Input
                  value={conditionDetail.force_skill}
                  onChange={(e) => updateConditionDetail({ force_skill: e.target.value })}
                  placeholder="BOSS_CODE_skill_name"
                />
              </Form.Item>
            )}
          </>
        );

      case 'HP':
        return (
          <>
            <Form.Item label="HP 임계값 (%)" required>
              <InputNumber
                min={0}
                max={100}
                value={conditionDetail.hp_threshold}
                onChange={(value) => updateConditionDetail({ hp_threshold: value ?? undefined })}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="강제 스킬">
              <Input
                value={conditionDetail.force_skill}
                onChange={(e) => updateConditionDetail({ force_skill: e.target.value })}
                placeholder="스킬 실행 시 사용할 스킬 키"
              />
            </Form.Item>
            <Form.Item label="현재 행동 중단">
              <Switch
                checked={conditionDetail.interrupt_current}
                onChange={(checked) => updateConditionDetail({ interrupt_current: checked })}
              />
            </Form.Item>
          </>
        );

      case 'SKILL_ACTION':
        return (
          <>
            <Form.Item label="대상 스킬" required>
              <Input
                value={conditionDetail.skill}
                onChange={(e) => updateConditionDetail({ skill: e.target.value })}
                placeholder="추적할 스킬 키"
              />
            </Form.Item>
            <Form.Item label="시전 횟수">
              <InputNumber
                min={1}
                value={conditionDetail.count}
                onChange={(value) => updateConditionDetail({ count: value ?? undefined })}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="교체할 스킬">
              <Input
                value={conditionDetail.replace_with}
                onChange={(e) => updateConditionDetail({ replace_with: e.target.value })}
                placeholder="N회 시전 후 교체할 스킬"
              />
            </Form.Item>
            <Form.Item label="카운터 리셋">
              <Switch
                checked={conditionDetail.reset_after}
                onChange={(checked) => updateConditionDetail({ reset_after: checked })}
              />
            </Form.Item>
          </>
        );

      case 'ENVIRONMENT':
        if (triggerSubtype === 'DISTANCE') {
          return (
            <>
              <Form.Item label="거리 타입">
                <Select
                  value={conditionDetail.distance_type}
                  onChange={(value) => updateConditionDetail({ distance_type: value })}
                >
                  <Select.Option value="first_aggro">첫 어그로 대상</Select.Option>
                  <Select.Option value="nearest">가장 가까운 대상</Select.Option>
                  <Select.Option value="farthest">가장 먼 대상</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="거리 임계값">
                <InputNumber
                  min={0}
                  value={conditionDetail.threshold}
                  onChange={(value) => updateConditionDetail({ threshold: value ?? undefined })}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="가까울 때 스킬">
                <Input
                  value={conditionDetail.skill_if_close}
                  onChange={(e) => updateConditionDetail({ skill_if_close: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="멀 때 스킬">
                <Input
                  value={conditionDetail.skill_if_far}
                  onChange={(e) => updateConditionDetail({ skill_if_far: e.target.value })}
                />
              </Form.Item>
            </>
          );
        }
        return (
          <Form.Item label="조건값">
            <Input
              value={conditionDetail.condition}
              onChange={(e) => updateConditionDetail({ condition: e.target.value })}
            />
          </Form.Item>
        );

      case 'COUNTER':
        return (
          <>
            <Form.Item label="카운터 이름" required>
              <Input
                value={conditionDetail.counter_name}
                onChange={(e) => updateConditionDetail({ counter_name: e.target.value })}
                placeholder="고유한 카운터 이름"
              />
            </Form.Item>
            <Form.Item label="목표 횟수">
              <InputNumber
                min={1}
                value={conditionDetail.target_count}
                onChange={(value) => updateConditionDetail({ target_count: value ?? undefined })}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="리셋 조건">
              <Select
                value={conditionDetail.reset_condition}
                onChange={(value) => updateConditionDetail({ reset_condition: value })}
              >
                <Select.Option value="none">없음</Select.Option>
                <Select.Option value="on_trigger">트리거 시 리셋</Select.Option>
                <Select.Option value="phase_change">페이즈 변경 시</Select.Option>
              </Select>
            </Form.Item>
          </>
        );

      default:
        return (
          <Form.Item label="JSON 직접 입력">
            <TextArea
              rows={4}
              value={JSON.stringify(conditionDetail, null, 2)}
              onChange={(e) => {
                try {
                  setConditionDetail(JSON.parse(e.target.value));
                } catch (error) {
                  // JSON 파싱 에러 무시
                }
              }}
            />
          </Form.Item>
        );
    }
  };

  const updateConditionDetail = (updates: Partial<TriggerConditionDetail>) => {
    const newDetail = { ...conditionDetail, ...updates };
    setConditionDetail(newDetail);
    form.setFieldsValue({ condition_detail: JSON.stringify(newDetail) });
  };

  // 초기값 로드
  useEffect(() => {
    const currentValue = form.getFieldValue('condition_detail');
    if (currentValue) {
      try {
        setConditionDetail(JSON.parse(currentValue));
      } catch (error) {
        setConditionDetail({});
      }
    }
  }, [form]);

  return (
    <Card size="small" title="트리거 조건 상세">
      <Space direction="vertical" style={{ width: '100%' }}>
        {renderConditionFields()}
        
        <Form.Item 
          label={
            <Space>
              <span>생성된 JSON</span>
              <InfoCircleOutlined />
            </Space>
          }
          style={{ marginTop: 16 }}
        >
          <TextArea
            rows={3}
            value={JSON.stringify(conditionDetail, null, 2)}
            readOnly
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          />
        </Form.Item>
      </Space>
    </Card>
  );
};

export default TriggerConditionSection;